import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SITE_URL, upsertLink, upsertMeta } from '../../seo/useSeo'

interface LegalPageProps {
  title: string
  content: string
  /** Route path, e.g. "/impressum" - used for the canonical tag. Passed explicitly rather than
   *  read via useLocation() so it stays correct even if this page is ever reached through a
   *  redirect/alias route. */
  path: string
}

/** Renders Impressum/Datenschutzerklärung as an actual app page instead of a static .txt file -
 *  serving plain text without an explicit charset let some browsers guess the wrong encoding and
 *  mangle umlauts, and it also couldn't carry the app's styling/navigation. The text itself stays
 *  German-only regardless of UI language (legal requirement, not a translation gap).
 *
 *  `noindex` on purpose (see docs/tasks/active/seo/seo-nach-recherche.md #7 audit): legally-required
 *  boilerplate that's near-identical across most German websites has no organic-search value and
 *  is a textbook thin/duplicate-content pattern - keeping it out of the index avoids diluting the
 *  site's overall quality signal. `follow` is kept so link equity still flows through the page's
 *  outgoing link. Consistently, this page is deliberately left out of sitemap.xml too (see
 *  scripts/generate-sitemap.mjs) - a noindex URL doesn't belong in a sitemap, which is meant to be
 *  an index-me request list. */
export function LegalPage({ title, content, path }: LegalPageProps) {
  const { t } = useTranslation()

  useEffect(() => {
    const canonicalUrl = `${SITE_URL}${path}`
    document.title = `${title} | Roofora`
    upsertMeta('name', 'robots', 'noindex, follow')
    upsertLink('canonical', canonicalUrl)
    return () => upsertMeta('name', 'robots', 'index, follow')
  }, [title, path])

  return (
    <div className="legal-page">
      <Link to="/" className="legal-page-back">
        {t('common.legal.back')}
      </Link>
      <h1>{title}</h1>
      <pre className="legal-page-content">{content}</pre>
    </div>
  )
}
